import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useTournamentFilters from './components/TournamentCasino/hook/useTournamentFilters';
import useTournamentSocket from './components/TournamentCasino/hook/useTournamentSocket';
import { openDialog, closeDialog } from '../../../../../store/actionCreators/dialog';
import { requestTournaments } from '../../../../../store/actionCreators/tournaments';
import { getSelectedTournamentCasino, getTournaments } from '../../../../../store/selectors';
import { requestTournamentCasinoRegister, requestTournamentCasinoUnregister } from '../../../../../store/actionCreators/tournamentCasinoRegister';
import TournamentCasinoRow from './components/TournamentCasino/TournamentCasinoRow';
import TournamentCasinoDetailsMobile from './components/TournamentCasino/TournamentCasinoDetails/TournamentCasinoDetailsMobile';
import TournamentGameWindow from './components/TournamentCasino/TournamentGameWindow';
import TournamentFilters from './components/TournamentCasino/TournamentFilters';
import TournamentLobby from './components/TournamentCasino/TournamentLobby/TournamentLobby';

const renderAfterMobile = (tour, selected, callbacks) => {
  if (selected !== tour.tournamentId) return false;

  return <TournamentCasinoDetailsMobile data={tour} {...callbacks} />;
}

const TournamentCasino = ({ selectedId, show, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tournaments = useSelector(getTournaments);
  const selectedTournamentCasino = useSelector(getSelectedTournamentCasino);
  const localSelectedTournamentId = (selectedTournamentCasino && selectedTournamentCasino.selectedId);
  const localSelectedShow = (selectedTournamentCasino && selectedTournamentCasino.show) || show;
  const [selectedTournamentId, setSelectedTournamentId] = useState(localSelectedTournamentId || tournaments[0] || null);
  const [showGame, setShowGame] = useState(null);
  const [showLobby, setShowLobby] = useState(false);
  const [payWithFPP, setPayWithFPP] = useState(false);
  const [showLobbyFromGame, setShowLobbyFromGame] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const selectedTournament = useMemo(
    () =>
      tournaments.find(t => t.tournamentId === selectedTournamentId) || tournaments[0] || null,
      [tournaments, selectedTournamentId]
  );

  const openLobbyCloseGame = () => {
    setShowGame(null);
    setShowLobby(true);
  }

  const backMainLobby = () => {
    setShowGame(null);
    setShowLobby(false);
  };

  const onLobbyFromGame = () => {
    setShowLobbyFromGame(true);
  }

  const onGameFromLobby = () => {
    setShowLobbyFromGame(false);
    setShowLobby(false);
    setShowInfo(false);
  }

  const openGame = useCallback((tour) => {
    setShowGame({
      id: tour.tournamentGames[0].id,
      tourId: tour.tournamentId,
    });
  },  [selectedTournament]);

  const onPlay = useCallback((tour) => {
    setSelectedTournamentId(tour.tournamentId);
    dispatch(openDialog(
      'tournamentCasinoStartInformation',
      {
        data: tour,
        onConfirm: () => dispatch(closeDialog()),
        btnNames: { confirm: t('Ok') },
      }
    ));
    openGame(tour);
  }, [selectedTournament])

 

  const onRegisterSuccess = useCallback((tour) => {
    dispatch(openDialog(
      'tournamentCasinoSuccessfull',
      {
        title: 'clubInfo',
        data: tour,
        onConfirm: () => {
          openGame(tour);
          dispatch(closeDialog());
        },
        onClose: () => dispatch(closeDialog()),
      }
    ));
  }, [selectedTournament]);

  const confirmRegister = useCallback((tour) => {
    dispatch(requestTournamentCasinoRegister({...tour}, onRegisterSuccess));
    dispatch(closeDialog());
  }, [selectedTournament]);

  const confirmUnregister = useCallback((tour) => {
    dispatch(requestTournamentCasinoUnregister({...tour}));
    dispatch(closeDialog());
  },[selectedTournament]);

  

  const onRegister = useCallback((tour) => {
    setSelectedTournamentId(tour.tournamentId);
    dispatch(openDialog(
      'tournamentCasinoRegister',
      {
        title: 'clubInfo',
        data: tour,
        onConfirm: () => confirmRegister(tour),
        onClose: () => dispatch(closeDialog()),
        btnNames: { confirm: t('register'), cancel: t('cancel') },
      }
    ));
  }, [selectedTournament]);
  
  const onUnregister = useCallback((tour) => {
    setSelectedTournamentId(tour.tournamentId);
    dispatch(openDialog(
      'tournamentCasinoUnregister',
      {
        title: 'clubInfo',
        data: tour,
        onConfirm: () => confirmUnregister(tour),
        onClose: () => dispatch(closeDialog()),
        btnNames: { confirm: t('unregister'), cancel: t('cancel') },
      }
    ))
  }, [selectedTournament]);

  const onLobby = useCallback((tour) => {
    setShowLobby(true);
  }, [selectedTournament]);


  const filters = useTournamentFilters(tournaments);

  useEffect(function initDefaultParameters() {
    if (localSelectedTournamentId) setSelectedTournamentId(localSelectedTournamentId);
  }, []);

  useEffect(() => {
    dispatch(requestTournaments());
  }, []);

  const selectTournament = useCallback((tour) => {
    selectedTournamentId === tour.tournamentId ? setSelectedTournamentId(null) : setSelectedTournamentId(tour.tournamentId);
  }, [selectedTournamentId]);

  const filteredTournaments = useMemo(() => {
    const { selectedTop, search, selectedState, selectedCategories } = filters;
    let ts = [...tournaments];
    switch (selectedTop) {
      case 'allTypes':
        break;
      case 'scheduledTournaments':
        ts = ts.filter(t => t.tournamentType === 'regular');
        break;
      case 'sngTournaments':
        ts = ts.filter(t => t.tournamentType === 'sitngo');
        break;
      case 'myTournaments':
        ts = ts.filter(t => t.playerStatus !== 'notRegistered');
      default:
        break;
    }

    if (search) {
      const regex = new RegExp(`(.*?)${search}(.*?)`, 'i');
      ts = ts.filter(t => regex.test(t.tournamentName));
    }

    if (selectedState !== 'allStates') {
      ts = ts.filter(t => t.tournamentState === selectedState);
    }
    if (selectedCategories !== 'allCategories') {
      ts = ts.filter(t => t.tournamentType === selectedCategories);
    }

    return ts;
  }, [tournaments, filters.selectedTop, filters.search, filters.selectedState, filters.selectedCategories])

  const gameFrameRef = useRef(null);

  const onShowBadBet = useCallback(tourId => {
    const tournament = tournaments.find(t => t.tournamentId === tourId);
    dispatch(openDialog(
      'tournamentCasinoBadBet',
      {
        tournament,
        onClose: () => dispatch(closeDialog()),
      }
    ));
    if (gameFrameRef.current) gameFrameRef.current.src += '';
  }, [tournaments]);

  const onServerMessage = data => {
    if (data.message === "tournament_started") {
      const startTournament = tournaments.find(t => t.tournamentId === data.tournamentId);
      dispatch(openDialog(
        'tournamentCasinoReminder',
        {
          data: startTournament,
          onClose: () => dispatch(closeDialog()),
          onConfirm: onPlay
        }
      ));
      return;
    }
    if (data.message === "tournament_finished") {
      // setFinishState(data);
      dispatch(openDialog(
        'tournamentCasinoFinished',
        {
          data: {data, onLobby: openLobbyCloseGame},
          smallSize: true,
          onClose: () => dispatch(closeDialog())
        }
      ));
      dispatch(requestTournaments());
      return;
    }
    dispatch(openDialog(
      'tournamentCasinoServerMessage',
      {
        message: typeof data === 'string' ? data : data.message,
        onClose: () => dispatch(closeDialog()),
      }
    ));
  };

  const emitToSocket = useTournamentSocket(
    onShowBadBet,
    onServerMessage,
    selectedTournament ? selectedTournament.tournamentId : null,
  );

  return (
    <div className="tournaments-casino">
      <TournamentFilters {...filters} tournaments={tournaments} t={t} />
      <ul className="tournaments-casino__list tournaments-casino__list--my-tournament">
      {tournaments.length > 0 && !!selectedTournament &&
        filteredTournaments.map((tournament, index) => (
          <TournamentCasinoRow 
            tour={tournament} 
            key={index} 
            selected={selectedTournamentId}
            renderAfter={renderAfterMobile}
            onPlay={onPlay}
            confirmUnregister={confirmUnregister}
            confirmRegister={confirmRegister}
            onRegister={onRegister}
            onUnregister={onUnregister}
            onLobby={onLobby}
            onClick={ selectTournament }
          />
        ))
      }
      </ul>
      {
        showGame && <TournamentGameWindow
          id={selectedTournamentId}
          game={showGame}
          tournamentCasino={tournaments}
          gameFrameRef={gameFrameRef}
          onClose={() => setShowGame(null)}
          onLobby={onLobby}
          onLobbyFromGame={onLobbyFromGame}
          emitToSocket={emitToSocket}
          setShowLobbyFromGame={setShowLobbyFromGame}
          showInfo={showInfo}
          setShowInfo={setShowInfo}
        />
      }
      {showLobby && <TournamentLobby 
        data={selectedTournament}
        onClose={() => setShowLobby(false)}
        onRegister={onRegister}
        onUnregister={onUnregister}
        onResume={() => {
          setShowLobby(false);
          onPlay(selectedTournament);
        }}
        onRefresh={() => selectedTournament ? emitToSocket('getPlayerList', { tourId: selectedTournament.tournamentId }) : false}
        isGameOpened={showGame}
        closeGameAndBackToLobby={backMainLobby}
        showLobbyFromGame={showLobbyFromGame}
        onGameFromLobby={onGameFromLobby}
        emitToSocket={emitToSocket}
      />}
    </div>
  )
}

export default TournamentCasino;